import React, { createContext, useContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import api from "../services/api";
import { ROLES } from "../constants/roles";

interface DecodedToken {
  id: number;
  email: string;
  role: string;
  name?: string;
  exp?: number;
}

export interface AppUser {
  id: number;
  name: string;
  email: string;
  role: string;
  isValidated?: boolean;
}

export interface MfaChallenge {
  email?: string;
  expirySeconds?: number;
  expiresAt?: string;
  otp?: string;
}

export interface LoginResult {
  success: boolean;
  needsPasswordReset?: boolean;
  accessDenied?: boolean;
  errorMessage?: string;
  requiresMfa?: boolean;
  mfaToken?: string;
  mfa?: MfaChallenge;
}

interface AuthContextType {
  user: AppUser | null;
  loginDoctor: (email: string, password: string) => Promise<LoginResult>;
  loginAdmin: (email: string, password: string) => Promise<LoginResult>;
  verifyMfaOtp: (mfaToken: string, otp: string) => Promise<LoginResult>;
  resendMfaOtp: (mfaToken: string) => Promise<LoginResult>;
  updatePassword: (newPassword: string) => Promise<boolean>;
  updateUser: (updates: Partial<AppUser>) => void;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Normalize backend role strings into our internal ROLES constants
const normalizeRole = (role: string | undefined | null): string => {
  if (!role) return "";
  const r = role.toLowerCase();

  if (r === "super_admin" || r === "superadmin") return ROLES.SUPERADMIN;
  // Future mappings for other roles can go here

  return role.toUpperCase();
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session - only restore if user is validated
  // Unvalidated users must log in again to check their current validation status
  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      const mockUser = localStorage.getItem("mockUser");

      if (mockUser) {
        const parsedMockUser = JSON.parse(mockUser);
        // Only restore mock users if they're validated
        if (parsedMockUser.isValidated === true) {
          setUser(parsedMockUser);
        } else {
          // Clear unvalidated mock users
          localStorage.removeItem("mockUser");
        }
      } else if (token) {
        const decoded = jwtDecode<DecodedToken>(token);
        // Try to get user data from localStorage to check isValidated
        const storedUser = localStorage.getItem("user");
        const userData = storedUser ? JSON.parse(storedUser) : null;
        
        // Only restore session if user is validated
        // Unvalidated users must log in again to check their current status from DB
        if (userData?.isValidated === true) {
          setUser({
            id: decoded.id,
            name: userData?.name || decoded.name || "Unknown",
            email: decoded.email,
            role: normalizeRole(decoded.role),
            isValidated: true,
          });
          
        } else {
          // Clear unvalidated sessions - force re-login
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
    } catch (err) {
      console.error("Auth restore error:", err);
      // clear invalid tokens/mocks
      localStorage.removeItem("token");
      localStorage.removeItem("mockUser");
      localStorage.removeItem("user");
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLoginResponse = async (response: any): Promise<LoginResult> => {
    const result = response.data;

    if (result?.success && result.data?.token) {
      const token = result.data.token;
      localStorage.setItem("token", token);

      const decoded = jwtDecode<DecodedToken>(token);

      const unifiedUser: AppUser = {
        id: decoded.id,
        name: decoded.name || "Unknown",
        email: decoded.email,
        role: normalizeRole(decoded.role),
        isValidated: true,
      };

      setUser(unifiedUser);
      localStorage.setItem("user", JSON.stringify(unifiedUser));
      return { success: true };
    }

    if (result?.success && result.data?.requiresMfa) {
      return {
        success: true,
        requiresMfa: true,
        mfaToken: result.data.mfaToken,
        mfa: { ...result.data.mfa, otp: result.data.otp },
      };
    }

    const errorMessage = result?.message || result?.data?.message || "Invalid email or password";
    return { success: false, errorMessage };
  };

  const loginDoctor = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await api.post("/auth/doctor/login", { email, password });
      return await handleLoginResponse(response);
    } catch (err: any) {
      console.error("Doctor login error:", err);
      const msg = err.response?.data?.message || "Invalid email or password";

      if (err.response?.status === 403) {
        if (msg.toLowerCase().includes('blocked') || msg.toLowerCase().includes('access denied')) {
          return { success: false, accessDenied: true, errorMessage: msg };
        }
        return { success: false, errorMessage: msg };
      }

      return { success: false, errorMessage: msg };
    }
  };

  const loginAdmin = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await api.post("/auth/admin/login", { email, password });
      return await handleLoginResponse(response);
    } catch (err: any) {
      console.error("Admin login error:", err);
      const msg = err.response?.data?.message || "Invalid email or password";

      if (err.response?.status === 403) {
        if (msg.toLowerCase().includes('blocked') || msg.toLowerCase().includes('access denied')) {
          return { success: false, accessDenied: true, errorMessage: msg };
        }
        return { success: false, errorMessage: msg };
      }

      return { success: false, errorMessage: msg };
    }
  };

  const verifyMfaOtp = async (mfaToken: string, otp: string): Promise<LoginResult> => {
    try {
      const response = await api.post("/auth/mfa/verify-otp", { mfaToken, otp });
      return await handleLoginResponse(response);
    } catch (err: any) {
      console.error("MFA verify error:", err);
      const msg = err.response?.data?.message || "Verification failed. Please try again.";
      return { success: false, errorMessage: msg };
    }
  };

  const resendMfaOtp = async (mfaToken: string): Promise<LoginResult> => {
    try {
      const response = await api.post("/auth/mfa/resend-otp", { mfaToken });
      const result = response.data;
      if (result?.success) {
        return { success: true, requiresMfa: true, mfaToken, mfa: { ...result.data?.mfa, otp: result.data?.otp } };
      }
      return { success: false, errorMessage: result?.message || "Failed to resend the verification code." };
    } catch (err: any) {
      console.error("MFA resend error:", err);
      return { success: false, errorMessage: err.response?.data?.message || "Failed to resend the verification code." };
    }
  };

  const updatePassword = async (newPassword: string): Promise<boolean> => {
    try {
      const response = await api.post("/auth/update-password", { newPassword });
      const result = response.data;

      if (result?.success) {
        // Update user state to mark as validated
        if (user) {
          const updatedUser: AppUser = {
            ...user,
            isValidated: true,
          };
          setUser(updatedUser);
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }
        return true;
      }

      return false;
    } catch (err: any) {
      console.error("Update password error:", err);
      return false;
    }
  };

  const updateUser = (updates: Partial<AppUser>) => {
    if (user) {
      const updatedUser: AppUser = {
        ...user,
        ...updates,
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("mockUser");
    localStorage.removeItem("user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loginDoctor, loginAdmin, verifyMfaOtp, resendMfaOtp, updatePassword, updateUser, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
